import { useEffect, useState } from "react";
import {
  completePublicationReview,
  getPublicationReview,
  listPublicationReviews,
  PublicationReviewDetail,
  PublicationReviewRow,
  recordPublicationReviewItem,
  runPublicationAutomatedChecks,
} from "@/api/methodologyConsole";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  EmptyState,
  ErrorState,
  HashLabel,
  LifecycleBadge,
  LoadingState,
  ReasonCodes,
} from "./primitives";

const itemBadge = (status: string) =>
  status === "passed" ? "default" : status === "failed" ? "destructive" : "secondary";

function ReviewDetail({ reviewId, onChanged }: { reviewId: string; onChanged: () => void }) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<PublicationReviewDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | {
    title: string;
    body: string;
    run: () => Promise<unknown>;
  }>(null);

  const load = () => {
    getPublicationReview(reviewId)
      .then(setDetail)
      .catch((e: Error) => setError(e.message));
  };
  useEffect(load, [reviewId]);

  const guard = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast({ title: ok });
      load();
      onChanged();
    } catch (e) {
      toast({
        title: "Refused by the server",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!detail) return <LoadingState label="Loading review…" />;

  const review = detail.review as Record<string, any>;
  const ready = Boolean(detail.readiness?.ready);
  const automated = detail.items.filter((i) => i.validation_mode === "automated");
  const human = detail.items.filter((i) => i.validation_mode !== "automated");

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span>Server readiness</span>
            <Badge variant={ready ? "default" : "destructive"}>
              {ready ? "Ready" : "Not ready"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            Review status: <strong>{String(review.review_status)}</strong> · Readiness hash{" "}
            <HashLabel value={review.readiness_hash} />
          </p>
          <ReasonCodes codes={(detail.readiness?.reason_codes as string[]) ?? []} />
          <p className="text-xs text-muted-foreground">
            Readiness is computed server-side by methodology_publication_readiness(). The
            console never derives or overrides it.
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() =>
            setConfirm({
              title: "Run automated checks?",
              body: "This re-runs the server-side automated validators for this review. It does not change lifecycle status.",
              run: () => runPublicationAutomatedChecks(reviewId),
            })
          }
        >
          Run automated checks
        </Button>
        <Button
          disabled={busy || !ready}
          title={ready ? undefined : "Disabled: server readiness is false"}
          onClick={() =>
            setConfirm({
              title: "Complete this publication review?",
              body: "The server re-verifies readiness before completing. This records you as the approving actor.",
              run: () => completePublicationReview(reviewId, null),
            })
          }
        >
          Complete review
        </Button>
        {!ready && (
          <span className="self-center text-xs text-muted-foreground">
            Completion is disabled until every required checklist item passes on the server.
          </span>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Automated checklist items ({automated.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {automated.length === 0 ? (
            <p className="text-sm text-muted-foreground">No automated items.</p>
          ) : (
            <ul className="space-y-2">
              {automated.map((i) => (
                <li key={i.item_id} className="flex items-start justify-between gap-3 border-b pb-2 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{i.item_label}</p>
                    <p className="text-xs text-muted-foreground">
                      {i.automated_validator ?? i.item_key} · read-only outcome
                    </p>
                  </div>
                  <Badge variant={itemBadge(i.status)}>{i.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Human and hybrid checklist items ({human.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {human.length === 0 ? (
            <p className="text-sm text-muted-foreground">No human items.</p>
          ) : (
            <ul className="space-y-4">
              {human.map((i) => (
                <li key={i.item_id} className="space-y-2 border-b pb-4 last:border-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{i.item_label}</p>
                      {i.guidance && (
                        <p className="text-xs text-muted-foreground">{i.guidance}</p>
                      )}
                      {i.completed_at && (
                        <p className="text-xs text-muted-foreground">
                          Recorded {new Date(i.completed_at).toLocaleString("en-US")}
                          {i.completed_by ? " by an authenticated administrator" : ""}
                        </p>
                      )}
                    </div>
                    <Badge variant={itemBadge(i.status)}>{i.status}</Badge>
                  </div>
                  <Textarea
                    rows={2}
                    placeholder="Reviewer notes (required where the checklist demands them)"
                    aria-label={`Notes for ${i.item_label}`}
                    value={notes[i.item_key] ?? i.reviewer_notes ?? ""}
                    onChange={(e) =>
                      setNotes((n) => ({ ...n, [i.item_key]: e.target.value }))
                    }
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        guard(
                          () =>
                            recordPublicationReviewItem(
                              reviewId,
                              i.item_key,
                              "passed",
                              notes[i.item_key] ?? null,
                            ),
                          "Item recorded as passed",
                        )
                      }
                    >
                      Record pass
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        guard(
                          () =>
                            recordPublicationReviewItem(
                              reviewId,
                              i.item_key,
                              "failed",
                              notes[i.item_key] ?? null,
                            ),
                          "Item recorded as failed",
                        )
                      }
                    >
                      Record fail
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                if (c) guard(c.run, "Server accepted the request");
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function PublicationReviewsTab() {
  const [rows, setRows] = useState<PublicationReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = () =>
    listPublicationReviews()
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    load();
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <LoadingState label="Loading publication reviews…" />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No publication reviews"
        description="No methodology artifact has an open or completed publication review. Reviews are opened through the governed review procedure."
      />
    );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artifact</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Artifact status</TableHead>
            <TableHead>Review status</TableHead>
            <TableHead>Checklist</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead className="text-right">Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.review_id}>
              <TableCell className="font-medium">
                {r.artifact_label ?? "Unnamed artifact"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {r.artifact_type.replace(/_/g, " ")}
              </TableCell>
              <TableCell>
                <LifecycleBadge status={r.artifact_status ?? undefined} />
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{r.review_status}</Badge>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {r.checklist_key ? `${r.checklist_key} ${r.checklist_version}` : "—"}
              </TableCell>
              <TableCell className="text-sm tabular-nums">
                {r.items_passed}/{r.items_total} passed
                {r.items_failed > 0 && (
                  <span className="text-destructive"> · {r.items_failed} failed</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => setOpenId(r.review_id)}>
                  Open
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Publication review</SheetTitle>
            <SheetDescription>
              Automated outcomes are read-only. Human sign-offs are recorded through governed
              procedures with the authenticated actor and timestamp.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {openId && <ReviewDetail reviewId={openId} onChanged={load} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
